import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { casinos } from '../data/casinos';
import CasinoCard from '../components/sections/CasinoCard';
import TrustSection from '../components/sections/TrustSection';
import FaqSection from '../components/sections/FaqSection';
import './Home.css';

const featuredCasinos = casinos.filter(c => c.featured);
const regularCasinos = casinos.filter(c => !c.featured);

const trustMetrics = [
  { value: '10+', label: 'Gennemgåede casinoer' },
  { value: '100%', label: 'Dansk licenserede' },
  { value: '4.8', label: 'Redaktionel kvalitetsscore' },
  { value: '2024', label: 'Opdateret senest' },
];

const reviewSteps = [
  {
    num: '01',
    title: 'Licens & Regulering',
    desc: 'Vi starter altid med at verificere, at casinoet har en gyldig dansk licens fra Spillemyndigheden. Uautoriserede udbydere omtaler vi ikke.'
  },
  {
    num: '02',
    title: 'Spiludvalg & Kvalitet',
    desc: 'Vi gennemgår sortimentet af slots, live casino, bordspil og eventuelle sportsbettingmuligheder. Vi kigger på softwareleverandører og spillekvalitet.'
  },
  {
    num: '03',
    title: 'Betalinger & Udbetalinger',
    desc: 'Vi tester ind- og udbetalingsprocesser, accepterede betalingsmetoder og – vigtigst – udbetalingshastighed og eventuelle gebyrer.'
  },
  {
    num: '04',
    title: 'Ansvarligt Spil',
    desc: 'Vi undersøger, hvilke spilbegrænsningsredskaber casinoet tilbyder: grænser, pause-funktion, selvudelukkelse og ROFUS-integration.'
  },
];

export default function Home() {
  return (
    <>
      <Helmet>
        <title>TopCasinoDansk – Uafhængig Casino Guide til Danske Spillere</title>
        <meta name="description" content="TopCasinoDansk er din uafhængige guide til de bedste licenserede online casinoer i Danmark. Professionelle anmeldelser, sammenligninger og spilguides på dansk." />
        <meta property="og:title" content="TopCasinoDansk – Uafhængig Casino Guide" />
        <meta property="og:description" content="Find det bedste licenserede online casino i Danmark. Uafhængige anmeldelser, ansvarligt spil og redaktionel vejledning." />
      </Helmet>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-bg-shapes" aria-hidden="true">
          <div className="hero-shape hero-shape-1"></div>
          <div className="hero-shape hero-shape-2"></div>
        </div>
        <div className="container">
          <div className="hero-grid">
            <div className="hero-content">
              <div className="hero-eyebrow">
                <span className="hero-badge">Uafhængig dansk guide</span>
                <span className="hero-age-marker">18+</span>
              </div>
              <h1>
                Redaktionel vejledning til{' '}
                <em className="hero-accent">danske casino­spillere</em>
              </h1>
              <p className="hero-desc">
                Vi sammenligner, anmelder og guider dig til de bedste licenserede online casinoer i Danmark.
                Alle anmeldelser er uafhængige og baseret på vores redaktionelle metode.
              </p>
              <div className="hero-actions">
                <a href="#casino-showcase" className="btn btn-primary btn-lg">
                  Se vores anbefalinger
                </a>
                <Link to="/vores-metode" className="btn btn-outline btn-lg">
                  Vores metode
                </Link>
              </div>
              <div className="hero-disclaimer">
                <span>Affiliate-oplysning:</span> Denne side bruger affiliate-links. Vores redaktionelle vurderinger er uafhængige.
              </div>
            </div>

            <div className="hero-cards-panel" aria-label="Udvalgte casinoer">
              <div className="hero-cards-label">Redaktionelt udvalg</div>
              {featuredCasinos.slice(0, 2).map((casino, i) => (
                <div key={casino.id} className={`hero-mini-card hero-mini-card--${i + 1}`}>
                  <div className="hero-mini-logo">
                    <img src={casino.logo} alt={casino.name}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                  <div className="hero-mini-info">
                    <span className="hero-mini-name">{casino.name}</span>
                    <span className="hero-mini-highlight">{casino.highlight}</span>
                    <div className="hero-mini-score">
                      <span className="hero-mini-score-num">{casino.rating.toFixed(1)}</span>
                      <span className="hero-mini-score-label">TopCasinoDansk score</span>
                    </div>
                  </div>
                  <a
                    href={casino.url}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="btn btn-primary btn-sm"
                  >
                    Besøg
                  </a>
                </div>
              ))}
              <div className="hero-cards-footer">
                <span>Se alle {casinos.length} anmeldte casinoer nedenfor</span>
              </div>
            </div>
          </div>

          {/* Trust bar */}
          <div className="hero-trust-bar">
            {trustMetrics.map((m, i) => (
              <div key={i} className="hero-trust-metric">
                <span className="hero-trust-value">{m.value}</span>
                <span className="hero-trust-label">{m.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Affiliate disclosure ribbon */}
      <div className="disclosure-ribbon">
        <div className="container">
          <p>
            <strong>Redaktionel uafhængighed:</strong> TopCasinoDansk er en selvstændig vejledningsportal og ikke tilknyttet nogen spiludbyder.
            Vores anmeldelser udformes ud fra egne kriterier. Vi modtager muligvis provision via affiliate-links – dette påvirker ikke vores vurderinger.
            Spil er kun tilladt for personer over 18 år med bopæl i Danmark.
          </p>
        </div>
      </div>

      {/* Casino Showcase */}
      <section id="casino-showcase" className="showcase-section section">
        <div className="container">
          <div className="showcase-header">
            <div className="divider"></div>
            <h2>Redaktionelt udvalgte casinoer</h2>
            <p className="section-subtitle">
              Vores team har gennemgået og vurderet følgende casinoer ud fra licens, spiludvalg, brugeroplevelse,
              udbetalinger og spillerbeskyttelse. Alle casinoer har gyldig dansk licens.
            </p>
          </div>

          {/* Featured casinos */}
          <div className="showcase-featured">
            {featuredCasinos.map((casino) => (
              <CasinoCard key={casino.id} casino={casino} variant="featured" />
            ))}
          </div>

          {/* Regular casino grid */}
          <div className="showcase-divider">
            <span>Flere anmeldte casinoer</span>
          </div>

          <div className="casino-grid">
            {regularCasinos.map((casino) => (
              <CasinoCard key={casino.id} casino={casino} variant="standard" />
            ))}
          </div>

          <div className="showcase-disclaimer">
            <span>18+ | Spil ansvarligt | Alle casinoer er licenseret af Spillemyndigheden</span>
          </div>
        </div>
      </section>

      {/* How We Review - compact */}
      <section className="review-method-section section bg-sand">
        <div className="container">
          <div className="review-method-header">
            <div className="divider"></div>
            <h2>Sådan vurderer vi casinoer</h2>
            <p className="section-subtitle">
              Vores redaktionelle metode er baseret på konkrete og målbare kriterier.
              Vi besøger, tester og vurderer hvert casino selvstændigt.
            </p>
            <Link to="/vores-metode" className="btn btn-outline" style={{ marginTop: '1rem' }}>
              Se vores fulde metode
            </Link>
          </div>

          <div className="review-steps">
            {reviewSteps.map((step) => (
              <div key={step.num} className="review-step">
                <div className="review-step-num">{step.num}</div>
                <div className="review-step-content">
                  <h4>{step.title}</h4>
                  <p>{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Danish Player Guide snippet */}
      <section className="guide-snippet section">
        <div className="container">
          <div className="guide-snippet-grid">
            <div className="guide-snippet-content">
              <div className="divider"></div>
              <h2>Guide til nye danske casinospillere</h2>
              <p>
                Overvejer du at prøve online casino for første gang? Det kan virke overvældende med så mange muligheder.
                Vores guide hjælper dig med at forstå, hvad du bør se efter – og hvad du bør undgå.
              </p>
              <ul className="guide-checklist">
                <li>
                  <span className="guide-check">✓</span>
                  <span>Vælg altid et casino med gyldig <strong>dansk licens</strong> fra Spillemyndigheden</span>
                </li>
                <li>
                  <span className="guide-check">✓</span>
                  <span>Sæt en <strong>personlig grænse</strong> for tid og penge, inden du starter</span>
                </li>
                <li>
                  <span className="guide-check">✓</span>
                  <span>Læs vilkårene for bonusser grundigt – <strong>omsætningskrav</strong> varierer meget</span>
                </li>
                <li>
                  <span className="guide-check">✓</span>
                  <span>Tjek betalingsmetoder og <strong>udbetalingstid</strong> inden registrering</span>
                </li>
                <li>
                  <span className="guide-check">✓</span>
                  <span>Kend dine rettigheder – og brug <strong>ROFUS</strong> hvis du har brug for pause</span>
                </li>
              </ul>
              <Link to="/casino-guide" className="btn btn-primary" style={{ marginTop: '1.5rem' }}>
                Læs den fulde casino guide
              </Link>
            </div>

            <div className="guide-snippet-aside">
              <div className="guide-fact-card">
                <h4>Vidste du?</h4>
                <ul className="guide-facts">
                  <li>
                    <span className="guide-fact-label">Licenseret i Danmark siden</span>
                    <span className="guide-fact-value">2012</span>
                  </li>
                  <li>
                    <span className="guide-fact-label">Antal licenserede casinoer</span>
                    <span className="guide-fact-value">50+</span>
                  </li>
                  <li>
                    <span className="guide-fact-label">ROFUS registrerede spillere</span>
                    <span className="guide-fact-value">70.000+</span>
                  </li>
                  <li>
                    <span className="guide-fact-label">Ansvarlig spiltjeneste</span>
                    <span className="guide-fact-value">StopSpillet.dk</span>
                  </li>
                </ul>
              </div>

              <div className="guide-safety-card">
                <div className="guide-safety-icon">🛡️</div>
                <h5>Spillerbeskyttelse i fokus</h5>
                <p>
                  Vi fremhæver altid casinoers ansvarlige spilredskaber og placerer spillersikkerhed øverst i vores vurderingskriterier.
                </p>
                <Link to="/ansvarligt-spil" className="guide-safety-link">
                  Læs om ansvarligt spil →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust & Safety Section */}
      <TrustSection />

      {/* FAQ */}
      <FaqSection />
    </>
  );
}
